/**
 * Glue between the authoritative server view and everything local:
 *
 *  - every accepted RoomView reconciles the draft (drop played tiles, return
 *    tiles whose square the opponent just took, append drawn tiles);
 *  - `diffViews` turns view changes into feedback: toasts, sounds, screen
 *    reader announcements, the "Your turn" pill, opponent score floats;
 *  - the tab title tracks whose turn it is;
 *  - the local "arranging tiles" activity is relayed to the opponent (only
 *    transitions are sent, and they are throttled).
 */
import { cellName, joinList } from '@wordloom/engine';
import type { Activity, RoomView } from '@wordloom/engine';
import { sfx } from '../audio/sfx';
import { rememberRoom } from '../lib/storage';
import { sendActivity } from '../net/api';
import { useDraftStore } from '../state/draftStore';
import { useRoomStore } from '../state/roomStore';
import { useUiStore } from '../state/uiStore';
import { dictCache } from './dict';
import { describeMove, diffViews, gameEndText, moveLabel, opponentSeat, seatName, type ViewEvent } from './events';

/** Align the local draft with a new view and report returned tiles. */
function syncDraft(view: RoomView): void {
  const draft = useDraftStore.getState();
  const game = view.game;
  if (!game) {
    if (draft.key !== null) draft.reset();
    return;
  }
  const result = draft.syncWithView(view.code, view.gameNumber, game);
  if (game.status === 'finished' && useDraftStore.getState().placements.length > 0) {
    useDraftStore.getState().recallAll();
  }
  if (result && result.returned.length > 0) {
    const names = result.returned.map((p) => cellName(p.row, p.col));
    const message =
      names.length === 1
        ? `Your tile on ${names[0]} was returned`
        : `Your tiles on ${joinList(names)} were returned`;
    useUiStore.getState().toast(message, { kind: 'warning' });
  }
}

/** Words that were actually played are known-good: seed the preview cache. */
function seedPlayedWords(prev: RoomView | null, next: RoomView): void {
  const history = next.game?.history ?? [];
  const from = prev?.code === next.code && prev.gameNumber === next.gameNumber ? (prev.game?.history.length ?? 0) : 0;
  for (const record of history.slice(from)) {
    for (const w of record.words) dictCache.seed(w.word, true);
  }
}

function applyEvents(events: ViewEvent[], view: RoomView): void {
  const ui = useUiStore.getState();
  const you = view.you;
  const opp = opponentSeat(view);
  const oppName = seatName(view, opp);
  const started = events.some((e) => e.type === 'game-started');

  for (const e of events) {
    switch (e.type) {
      case 'opponent-joined':
        // When the game starts right away, the "Game on!" pill says it all.
        if (!started) ui.toast(`${e.name} joined`, { kind: 'success' });
        break;
      case 'game-started': {
        const first = e.firstSeat === you ? 'You go first' : `${seatName(view, e.firstSeat)} goes first`;
        ui.setGameOverDismissed(false);
        // "Alex wants a rematch!" is answered once the rematch is on.
        ui.dismissToast('rematch');
        ui.showBanner({ kind: 'start', title: e.gameNumber > 1 ? 'Rematch!' : 'Game on!', detail: first });
        ui.announce(`${e.gameNumber > 1 ? 'Rematch started' : 'Game on'}. ${first}.`);
        if (e.firstSeat === you) sfx.turn();
        break;
      }
      case 'move': {
        const name = e.mine ? 'You' : seatName(view, e.record.seat);
        const text = describeMove(e.record, name);
        if (e.mine) {
          // My plays are celebrated from the submit ack; only a timeout needs a word.
          if (e.record.type === 'timeout') ui.toast('Time’s up — your turn was passed.', { kind: 'warning' });
          break;
        }
        ui.announce(moveLabel(e.record, name));
        if (e.record.type === 'play') {
          sfx.place();
          if (e.record.score > 0) {
            ui.celebrate({
              kind: 'score',
              score: e.record.score,
              seat: e.record.seat,
              cells: e.record.tiles.map((t) => ({ row: t.row, col: t.col })),
            });
          }
        } else if (e.record.type === 'pass' || e.record.type === 'exchange' || e.record.type === 'timeout') {
          ui.toast(text, { kind: 'info' });
        }
        break;
      }
      case 'my-turn':
        if (started) break; // the "Game on" pill already says who starts
        sfx.turn();
        ui.showBanner({ kind: 'turn', title: 'Your turn' });
        ui.announce('Your turn.');
        break;
      case 'game-ended': {
        const { winner } = e.result;
        sfx.gameOver(winner === you);
        ui.setGameOverDismissed(false);
        if (winner === you) ui.celebrate({ kind: 'win', score: 0, seat: you, cells: [] });
        ui.announce(gameEndText(e.result, view), 'assertive');
        break;
      }
      case 'opponent-disconnected':
        ui.announce(`${e.name} disconnected.`);
        break;
      case 'opponent-reconnected':
        ui.toast(`${e.name} is back`, { kind: 'success' });
        break;
      case 'rematch-requested':
        ui.toast(`${e.name} wants a rematch!`, { kind: 'info', id: 'rematch' });
        break;
      case 'my-rematch-vote':
        ui.announce(e.want ? `Rematch requested — waiting for ${oppName}.` : 'Rematch request withdrawn.');
        break;
    }
  }
}

/** "● Your turn — Wordloom" while it is my move. */
export function titleFor(view: RoomView | null): string {
  if (!view) return 'Wordloom';
  const game = view.game;
  if (view.status === 'waiting' || !game) return `Room ${view.code} — Wordloom`;
  if (game.status === 'finished') return 'Game over — Wordloom';
  if (game.currentSeat === view.you) return '● Your turn — Wordloom';
  return `${seatName(view, game.currentSeat)}’s turn — Wordloom`;
}

function onView(prev: RoomView | null, next: RoomView | null): void {
  document.title = titleFor(next);
  if (!next) return;
  syncDraft(next);
  seedPlayedWords(prev, next);
  const opp = next.seats[opponentSeat(next)];
  if (opp && (!prev || prev.seats[opponentSeat(prev)]?.name !== opp.name)) rememberRoom(next.code, opp.name);
  applyEvents(diffViews(prev, next), next);
}

// ---------------------------------------------------------------------------
// Activity relay
// ---------------------------------------------------------------------------

const ACTIVITY_THROTTLE_MS = 400;
let lastSent: Activity = 'idle';
let activityTimer: ReturnType<typeof setTimeout> | null = null;

function desiredActivity(): Activity {
  const view = useRoomStore.getState().view;
  if (view?.game?.status !== 'playing') return 'idle';
  return useDraftStore.getState().placements.length > 0 ? 'arranging' : 'idle';
}

function scheduleActivity(): void {
  if (activityTimer) return;
  activityTimer = setTimeout(() => {
    activityTimer = null;
    const want = desiredActivity();
    if (want !== lastSent && useRoomStore.getState().session?.joined) {
      lastSent = want;
      sendActivity(want);
    }
  }, ACTIVITY_THROTTLE_MS);
}

/** Install once. Returns an uninstall function (tests, mock teardown). */
export function installViewSync(): () => void {
  const unsubRoom = useRoomStore.subscribe((state, prev) => {
    if (state.view !== prev.view) onView(prev.view, state.view);
    // A fresh connection starts from 'idle' on the server side.
    if (state.session?.joined && !prev.session?.joined) lastSent = 'idle';
    scheduleActivity();
  });
  const unsubDraft = useDraftStore.subscribe((state, prev) => {
    if (state.placements.length !== prev.placements.length) scheduleActivity();
  });
  return () => {
    unsubRoom();
    unsubDraft();
    if (activityTimer) clearTimeout(activityTimer);
    activityTimer = null;
  };
}
